import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Platform, View } from 'react-native';

import {
    addHours,
    endOfDay,
    isAfter,
    isBefore,
    isWithinInterval,
    startOfDay,
    subHours,
} from 'date-fns';
import { Subscription } from 'rxjs';

import { EPGConfig } from 'custom_modules/react-native-epg';

import ticker$ from 'utils/hooks/useInterval';
import { isTablet } from 'utils/responsiveness';
import scrollPositionToTime from 'utils/scrollPositionToTime';

import EpgJumpToDayMarker from 'screens/Epg/components/EpgJumpMarkers/EpgJumpToDayMarker';
import EpgNowJumpMarker from 'screens/Epg/components/EpgJumpMarkers/EpgNowJumpMarker';
import { PRIME_TIME } from 'screens/Epg/utils/config';
import { ArrowDirection } from 'screens/Epg/utils/epg-config';

import styles from './styles';

type Props = {
    config: Partial<EPGConfig>;
    visible?: boolean;
    scrollPos: number;
    selectedDate: number;
    prevDateLabel: string;
    nextDateLabel: string;
    onPressPrevDateJumpMarker: () => void;
    onPressNextDateJumpMarker: () => void;
    onPressPrimeTimeJumpMarker: () => void;
    onPressNowJumpMarker: () => void;
};

const EpgJumpMarkers = ({
    config,
    visible = true,
    scrollPos,
    selectedDate,
    prevDateLabel,
    nextDateLabel,
    onPressNextDateJumpMarker,
    onPressPrevDateJumpMarker,
    onPressNowJumpMarker,
    onPressPrimeTimeJumpMarker,
}: Props) => {
    const [visibleAfterConfigChange, setVisibleAfterConfigChange] =
        useState(true);

    const [visibleAfterPress, setVisibleAfterPress] = useState(true);

    const [scrollPosition, setScrollPosition] = useState(scrollPos);

    const [now, setNow] = useState(new Date());

    const ticker = useRef<Subscription | null>(null);

    useEffect(() => {
        setScrollPosition(scrollPos);
    }, [scrollPos]);

    const onTick = useCallback(() => {
        setNow(new Date());
    }, []);

    useEffect(() => {
        if (ticker.current === null) {
            ticker.current = ticker$(30000).subscribe(() => onTick());
        }

        return () => {
            if (ticker.current) {
                ticker.current.unsubscribe();
                ticker.current = null;
            }
        };
    }, [onTick]);

    const start = scrollPositionToTime(
        selectedDate,
        Math.max(0, scrollPosition) / config.hourWidth!,
    );
    const end = scrollPositionToTime(
        selectedDate,
        (scrollPosition +
            (isTablet || Platform.OS === 'web' ? 2 : 0.75) *
                config.hourWidth!) /
            config.hourWidth!,
    );

    const markerPositionLeft = useMemo(
        () => config.channelWidth || 0,
        [config],
    );

    const prevDayMarkerShouldBeVisible =
        prevDateLabel &&
        isWithinInterval(start, {
            start: startOfDay(selectedDate),
            end: addHours(startOfDay(selectedDate), 1),
        });

    const nextDayMarkerShouldBeVisible =
        nextDateLabel &&
        isWithinInterval(end, {
            start: subHours(endOfDay(selectedDate), 1),
            end: endOfDay(selectedDate),
        });

    const rightNowJumpMarkerShouldBeVisible =
        !isWithinInterval(now, {
            start,
            end,
        }) &&
        !nextDayMarkerShouldBeVisible &&
        isBefore(start, now);

    useEffect(() => {
        setVisibleAfterConfigChange(false);
        setScrollPosition(0);
        const timeoutId = setTimeout(() => {
            setVisibleAfterConfigChange(true);
        }, 300);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [config]);

    const handleVisibilityAfterPress = useCallback(() => {
        setVisibleAfterPress(false);
        const timeoutId = setTimeout(() => {
            setVisibleAfterPress(true);
        }, 700);

        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        const cleanup = handleVisibilityAfterPress;

        return () => {
            cleanup();
        };
    }, [handleVisibilityAfterPress]);

    const onPressNextDateJumpMarkerCallback = useCallback(() => {
        onPressNextDateJumpMarker();
        handleVisibilityAfterPress();
    }, [onPressNextDateJumpMarker, handleVisibilityAfterPress]);

    const onPressPrevDateJumpMarkerCallback = useCallback(() => {
        onPressPrevDateJumpMarker();
        handleVisibilityAfterPress();
    }, [onPressPrevDateJumpMarker, handleVisibilityAfterPress]);

    const onPressNowJumpMarkerCallback = useCallback(() => {
        onPressNowJumpMarker();
        handleVisibilityAfterPress();
    }, [onPressNowJumpMarker, handleVisibilityAfterPress]);

    const onPressPrimeTimeJumpMarkerCallback = useCallback(() => {
        onPressPrimeTimeJumpMarker();
        handleVisibilityAfterPress();
    }, [onPressPrimeTimeJumpMarker, handleVisibilityAfterPress]);

    const leftNowJumpMarkerShouldBeVisible =
        !isWithinInterval(now, {
            start,
            end,
        }) &&
        !prevDayMarkerShouldBeVisible &&
        isAfter(start, now);

    const primeTimeJumpMarkerShouldBeVisible =
        PRIME_TIME &&
        !rightNowJumpMarkerShouldBeVisible &&
        !nextDayMarkerShouldBeVisible &&
        !isWithinInterval(addHours(startOfDay(selectedDate), PRIME_TIME), {
            start,
            end,
        }) &&
        isBefore(end, addHours(startOfDay(selectedDate), PRIME_TIME));

    const LeftJumpMarker = useMemo(() => {
        if (prevDayMarkerShouldBeVisible) {
            return (
                <EpgJumpToDayMarker
                    dateLabel={prevDateLabel}
                    onPressMarker={onPressPrevDateJumpMarkerCallback}
                />
            );
        }

        if (leftNowJumpMarkerShouldBeVisible) {
            return (
                <EpgNowJumpMarker
                    direction={ArrowDirection.LEFT}
                    onPressMarker={onPressNowJumpMarkerCallback}
                />
            );
        }

        return null;
    }, [
        prevDayMarkerShouldBeVisible,
        leftNowJumpMarkerShouldBeVisible,
        prevDateLabel,
        onPressNowJumpMarkerCallback,
        onPressPrevDateJumpMarkerCallback,
    ]);

    const RightJumpMarker = useMemo(() => {
        if (nextDayMarkerShouldBeVisible) {
            return (
                <EpgJumpToDayMarker
                    dateLabel={nextDateLabel}
                    direction={ArrowDirection.RIGHT}
                    onPressMarker={onPressNextDateJumpMarkerCallback}
                />
            );
        }

        if (rightNowJumpMarkerShouldBeVisible) {
            return (
                <EpgNowJumpMarker
                    direction={ArrowDirection.RIGHT}
                    onPressMarker={onPressNowJumpMarkerCallback}
                />
            );
        }

        if (primeTimeJumpMarkerShouldBeVisible) {
            return null;
        }

        return null;
    }, [
        nextDayMarkerShouldBeVisible,
        nextDateLabel,
        rightNowJumpMarkerShouldBeVisible,
        onPressNowJumpMarkerCallback,
        primeTimeJumpMarkerShouldBeVisible,
        onPressPrimeTimeJumpMarkerCallback,
        onPressNextDateJumpMarkerCallback,
    ]);

    if (!visible || !visibleAfterConfigChange || !visibleAfterPress) {
        return null;
    }

    return (
        <>
            <View
                style={[
                    styles.container,
                    styles.leftMarker,
                    {
                        height: config.timebarHeight,
                        marginLeft: markerPositionLeft,
                    },
                ]}
            >
                {LeftJumpMarker}
            </View>
            <View
                style={[
                    styles.container,
                    styles.rightMarker,
                    {
                        height: config.timebarHeight,
                    },
                ]}
            >
                {RightJumpMarker}
            </View>
        </>
    );
};

export default EpgJumpMarkers;
